import { useAuth } from "@/auth/AuthContext"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Link } from "@tanstack/react-router"
import { LogOut, User as UserA } from "lucide-react"
import toast from "react-hot-toast"


export function ProfileMenu() {
  const auth = useAuth()
  // Get initials for avatar fallback
  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
  }

  const isImpersonating = localStorage.getItem("impersonateEmail") !== null;
  const isApiKeyAuth = auth?.authMethod === "apikey";

  const logout = async () => {
    if (isApiKeyAuth) {
      auth?.logout()
      return
    }

    try {

      const url = `${import.meta.env.VITE_API_URL}/auth/logout`

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${auth?.user?.access_token}`,
        },
      })

      if (!response.ok) {
        throw new Error("Network response was not ok")
      }

      auth?.logout()
    } catch (error) {
      toast.error("Logout failed. Please try again.")
    }
  }

  const stopImpersonation = () => {
    localStorage.removeItem("impersonateEmail");
    toast.success("Stopped impersonation. Please log in again if you want to continue.");
    //refresh page 
    window.location.reload();
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="relative h-12 w-12 rounded-full">
          <Avatar className="h-8 w-8">
            <AvatarImage src={ "/placeholder.svg"} alt={auth?.user?.profile.name ?? "API Key"} />
            <AvatarFallback>{isApiKeyAuth ? "AK" : getInitials(auth?.user?.profile.name ?? "")}</AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56" align="end" forceMount>
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium leading-none">{isApiKeyAuth ? "API Key" : auth?.user?.profile.name}</p>
            {/* @ts-ignore */}
            <p className="text-xs leading-none text-muted-foreground">{isApiKeyAuth ? "" : (auth?.user?.profile?.upn ?? "")}</p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <Link to='/profile'>
          <DropdownMenuItem>
            <UserA className="mr-2 h-4 w-4" />
            <span>Profile</span>
          </DropdownMenuItem>
          </Link>
        </DropdownMenuGroup>
        {isImpersonating && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={stopImpersonation}>
              <LogOut className="mr-2 h-4 w-4" />
              <span>Stop Impersonating</span>
            </DropdownMenuItem>
          </>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={logout}>
          <LogOut className="mr-2 h-4 w-4" />
          <span>Log out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
